/**
 * ConsentLanding — magic invite link landing page.
 *
 * The most-seen surface by people who aren't paying users (per
 * /shared/magic_invite_link_contract.md). Trust posture is everything here:
 * tool framing (coaching, not surveillance), one-tap consent flow, decline
 * equally prominent, expandable "what does my manager see / not see / can I
 * revoke?" sections.
 *
 * Per /policy/brand-discipline.md §11 fairness/dignity: no marketing copy,
 * no surveillance language, asymmetric vantage flipped — the recipient is
 * the active party with agency.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Wordmark, Callout } from "../components/index.js";

// Production-domain readiness 2026-05-04: env-driven base URL so user-facing
// "revoke at <BASE>/revoke" copy matches the deployed environment. Falls
// back to peopleprints.app for production. For staging, set
// VITE_APP_BASE_URL=https://peopleprints-staging.netlify.app at build time.
const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || "https://peopleprints.app";

export default function ConsentLanding() {
  const { token } = useParams();
  const [state, setState] = useState("loading");      // loading | ready | confirmed | declined | error | expired | already_redeemed
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dob, setDob] = useState({ month: "", day: "", year: "" });
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!token) {
      setState("error");
      setError("Missing token in URL.");
      return;
    }
    fetch(`/.netlify/functions/consent-landing-data?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) {
          if (r.status === 410) setState("expired");
          else if (r.status === 409) setState("already_redeemed");
          else setState("error");
          setError(body.message || body.error || "Could not load this invite.");
          return;
        }
        setData(body);
        setState("ready");
      })
      .catch((e) => {
        setState("error");
        setError(e.message);
      });
  }, [token]);

  const submit = async (decision) => {
    if (decision === "consent") {
      if (!dob.month || !dob.day) {
        setError("Please enter at least the month and day.");
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/consent-redeem", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token,
          decision,
          dob_year: decision === "consent" ? (dob.year ? parseInt(dob.year, 10) : null) : undefined,
          dob_month: decision === "consent" ? parseInt(dob.month, 10) : undefined,
          dob_day: decision === "consent" ? parseInt(dob.day, 10) : undefined,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        if (res.status === 409) setState("already_redeemed");
        else setError(body.message || body.error || "Submission failed.");
        return;
      }
      setState(decision === "consent" ? "confirmed" : "declined");
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  return (
    <div
      style={{
        maxWidth: 560,
        margin: "0 auto",
        padding: "32px 20px 80px",
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.1} />
      </div>

      {state === "loading" && <Loading />}

      {state === "expired" && (
        <ErrorState
          headline="This link has expired."
          body="Ask the person who invited you to send a new one."
        />
      )}

      {state === "already_redeemed" && (
        <ErrorState
          headline="This link has already been used."
          body="If you're trying to revoke or update consent, contact the person who invited you."
        />
      )}

      {state === "error" && (
        <ErrorState headline="Something isn't right with this link." body={error || "Please contact the person who invited you."} />
      )}

      {state === "ready" && data && (
        <>
          <h1
            style={{
              fontFamily: "var(--ppr-typography-font-family-display)",
              fontSize: "var(--ppr-typography-font-size-3xl)",
              color: "var(--ppr-color-accent-navy)",
              letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
              lineHeight: "var(--ppr-typography-line-height-tight)",
              margin: "0 0 12px",
            }}
          >
            Hi {data.recipient_first_name}.
          </h1>
          <p
            style={{
              fontFamily: "var(--ppr-typography-font-family-body)",
              fontSize: "var(--ppr-typography-font-size-md)",
              lineHeight: "var(--ppr-typography-line-height-relaxed)",
              color: "var(--ppr-color-text-primary)",
              margin: "0 0 8px",
            }}
          >
            <strong>{data.tenant.display_name}</strong> would like to set up a Interaction Engineering profile for you.
          </p>
          <p
            style={{
              fontFamily: "var(--ppr-typography-font-family-body)",
              fontSize: "var(--ppr-typography-font-size-base)",
              lineHeight: "var(--ppr-typography-line-height-relaxed)",
              color: "var(--ppr-color-text-secondary)",
              margin: "0 0 24px",
            }}
          >
            Interaction Engineering is a preparation tool the person who invited you uses to have better conversations with you. It's not an evaluation, not a decision-making tool, and not a surveillance system.
          </p>

          <Disclosure
            label="What does the sender see?"
            open={expanded.see}
            onToggle={() => toggle("see")}
          >
            A structured "Operating Manual" the sender can read before talking with you. Twelve sections covering communication preferences, recognition style, what motivates you, the kind of feedback that lands, and how the two of you work together. They never see anything that would be a surprise if you read it yourself.
          </Disclosure>

          <Disclosure
            label="What does the sender NOT see?"
            open={expanded.not_see}
            onToggle={() => toggle("not_see")}
          >
            Your profile is not used for any formal decision about you, and is not visible to anyone else without explicit permission. The sender's private notes about you are never visible to others.
          </Disclosure>

          <Disclosure
            label="Can I see my own profile?"
            open={expanded.self}
            onToggle={() => toggle("self")}
          >
            Yes, in many cases. Whether self-view is enabled is up to the sender. You can ask them.
          </Disclosure>

          <Disclosure
            label="Can I revoke later?"
            open={expanded.revoke}
            onToggle={() => toggle("revoke")}
          >
            Yes, anytime. Reach out to {data.tenant.display_name} or visit <code>{APP_BASE_URL.replace(/^https?:\/\//, "")}/revoke</code>. We honor revocations within 30 days.
          </Disclosure>

          <Callout.Tip>
            All we collect today is your <strong>date of birth</strong>. Year is optional — you'll get a better profile if you include it, but month and day are enough.
          </Callout.Tip>

          <DateOfBirthInputs value={dob} onChange={setDob} />

          {error && (
            <div
              style={{
                marginTop: 12,
                fontFamily: "var(--ppr-typography-font-family-body)",
                fontSize: "var(--ppr-typography-font-size-sm)",
                color: "var(--ppr-color-accent-coral-dark)",
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexDirection: "column", marginTop: 20 }}>
            <ConfirmButton onClick={() => submit("consent")} disabled={submitting} />
            <DeclineButton onClick={() => submit("decline")} disabled={submitting} />
          </div>

          <p
            style={{
              marginTop: 24,
              fontFamily: "var(--ppr-typography-font-family-mono)",
              fontSize: "var(--ppr-typography-font-size-xs)",
              color: "var(--ppr-color-text-muted)",
            }}
          >
            Link expires {new Date(data.expires_at).toLocaleDateString()}
          </p>
        </>
      )}

      {state === "confirmed" && (
        <Outcome
          headline="Thanks. You're set up."
          body={`${data?.tenant?.display_name || "Your team"} now sees your profile. You can revoke consent any time at ${APP_BASE_URL.replace(/^https?:\/\//, "")}/revoke.`}
        />
      )}

      {state === "declined" && (
        <Outcome
          headline="You declined. That's it — nothing else."
          body="No profile is created. The sender won't be notified that you declined."
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function Loading() {
  return (
    <p
      style={{
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        color: "var(--ppr-color-text-muted)",
      }}
    >
      Loading…
    </p>
  );
}

function ErrorState({ headline, body }) {
  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-2xl)",
          color: "var(--ppr-color-accent-coral-dark)",
          margin: "0 0 12px",
        }}
      >
        {headline}
      </h1>
      <p style={{ fontFamily: "var(--ppr-typography-font-family-body)" }}>{body}</p>
    </div>
  );
}

function Outcome({ headline, body }) {
  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-2xl)",
          color: "var(--ppr-color-accent-navy)",
          letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
          margin: "0 0 12px",
        }}
      >
        {headline}
      </h1>
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: "var(--ppr-color-text-secondary)",
        }}
      >
        {body}
      </p>
    </div>
  );
}

function Disclosure({ label, open, onToggle, children }) {
  return (
    <div
      style={{
        borderTop: "1px solid var(--ppr-color-border-default)",
        padding: "12px 0",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-navy)",
        }}
        aria-expanded={!!open}
      >
        {open ? "−" : "+"}  {label}
      </button>
      {open && (
        <p
          style={{
            marginTop: 8,
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-base)",
            lineHeight: "var(--ppr-typography-line-height-relaxed)",
            color: "var(--ppr-color-text-secondary)",
          }}
        >
          {children}
        </p>
      )}
    </div>
  );
}

function DateOfBirthInputs({ value, onChange }) {
  const inputStyle = {
    padding: "12px 14px",
    fontFamily: "var(--ppr-typography-font-family-ui)",
    fontSize: "var(--ppr-typography-font-size-base)",
    border: "1px solid var(--ppr-color-border-default)",
    background: "var(--ppr-color-background-surface)",
    color: "var(--ppr-color-text-primary)",
    width: "100%",
    outline: "none",
  };
  // Month dropdown spec (Russ 2026-05-07 e2e testing tonight): Day + Year
  // stay numeric inputs (1-2 digit / 4-digit typing is the right interaction)
  // but Month is awkward as free-text — typo risk, "13" passes basic
  // numeric validation, etc. Select with month-name labels + numeric values
  // (1-12). Backend partial-DOB handling reads `parseInt(dob.month, 10)`
  // unchanged. Placeholder option is disabled+selected so it can't be
  // submitted.
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 10, marginTop: 16 }}>
      <select
        value={value.month}
        onChange={(e) => onChange({ ...value, month: e.target.value })}
        style={{ ...inputStyle, cursor: "pointer" }}
        aria-label="Month"
      >
        <option value="" disabled>Month</option>
        {MONTH_OPTIONS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <input
        type="number" min="1" max="31" placeholder="Day"
        value={value.day}
        onChange={(e) => onChange({ ...value, day: e.target.value })}
        style={inputStyle} aria-label="Day"
      />
      <input
        type="number" min="1900" max="2100" placeholder="Year (optional)"
        value={value.year}
        onChange={(e) => onChange({ ...value, year: e.target.value })}
        style={inputStyle} aria-label="Year"
      />
    </div>
  );
}

// Month dropdown options. Numeric values (1-12) keep the existing
// backend partial-DOB handling unchanged; the human-readable label is
// only for the dropdown UI.
const MONTH_OPTIONS = [
  { value: "1",  label: "January" },
  { value: "2",  label: "February" },
  { value: "3",  label: "March" },
  { value: "4",  label: "April" },
  { value: "5",  label: "May" },
  { value: "6",  label: "June" },
  { value: "7",  label: "July" },
  { value: "8",  label: "August" },
  { value: "9",  label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function ConfirmButton({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "14px 18px",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        border: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-accent-navy)",
        color: "var(--ppr-color-text-inverse)",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      Confirm — set up my profile
    </button>
  );
}

function DeclineButton({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "14px 18px",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        border: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-background-surface)",
        color: "var(--ppr-color-accent-navy)",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      Decline — don't set up a profile
    </button>
  );
}
